import { useState } from "react";
import type { OfflineCodeResult } from "@/pages/admin/tools/hooks/useLicenseTools";

interface OfflineGeneratorProps {
  notify: (text: string, tone?: "success" | "error") => void;
  onGenerate: (machineCode: string, expiresAt: string | null) => Promise<OfflineCodeResult>;
  onRevealPublicKey: () => Promise<string>;
}

const STEPS = [
  { icon: "ri-key-line", title: "取公钥", text: "在下方复制「离线验签公钥」，内置到打包软件的客户端代码中。" },
  { icon: "ri-computer-line", title: "客户提供机器码", text: "打包软件在客户电脑上算出的机器码，发给作者。" },
  { icon: "ri-signpost-line", title: "后台私钥签名", text: "作者输入机器码，后台用私钥立即签出激活码（私钥不出库）。" },
  { icon: "ri-shield-check-line", title: "公钥离线验签", text: "客户粘贴激活码，软件用内置公钥本地验签，无需联网、防篡改。" },
];

export default function OfflineGenerator({ notify, onGenerate, onRevealPublicKey }: OfflineGeneratorProps) {
  const [machineCode, setMachineCode] = useState("");
  const [expiresAt, setExpiresAt] = useState("");
  const [generating, setGenerating] = useState(false);
  const [result, setResult] = useState<OfflineCodeResult | null>(null);

  const [publicKey, setPublicKey] = useState<string | null>(null);
  const [revealing, setRevealing] = useState(false);

  const handleGenerate = async () => {
    const code = machineCode.trim();
    if (!code) {
      notify("请先填写客户机器的机器码", "error");
      return;
    }
    setGenerating(true);
    try {
      const res = await onGenerate(code, expiresAt ? `${expiresAt}T23:59:59` : null);
      setResult(res);
      notify("已生成离线激活码", "success");
    } catch (err) {
      notify(err instanceof Error ? err.message : "生成失败", "error");
    } finally {
      setGenerating(false);
    }
  };

  const handleReveal = async () => {
    if (publicKey) {
      setPublicKey(null);
      return;
    }
    setRevealing(true);
    try {
      const value = await onRevealPublicKey();
      setPublicKey(value);
    } catch (err) {
      notify(err instanceof Error ? err.message : "获取公钥失败", "error");
    } finally {
      setRevealing(false);
    }
  };

  const copy = async (text: string, label: string) => {
    try {
      await navigator.clipboard.writeText(text);
      notify(`已复制${label}`, "success");
    } catch {
      notify("复制失败，请手动选择复制", "error");
    }
  };

  return (
    <div className="space-y-6">
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {STEPS.map((step, index) => (
          <div key={step.title} className="rounded-lg border border-background-200 bg-background-50 p-4">
            <div className="flex items-center justify-between">
              <span className="flex h-9 w-9 items-center justify-center rounded-md bg-accent-100 text-accent-700">
                <i className={step.icon} />
              </span>
              <span className="font-mono text-xs text-foreground-400">0{index + 1}</span>
            </div>
            <h3 className="mt-3 text-sm font-semibold text-foreground-950">{step.title}</h3>
            <p className="mt-1 text-xs leading-relaxed text-foreground-600">{step.text}</p>
          </div>
        ))}
      </div>

      <div className="rounded-lg border border-background-200 bg-background-50 p-5">
        <div className="flex items-center gap-2">
          <span className="flex h-8 w-8 items-center justify-center rounded-md bg-primary-100 text-primary-700">
            <i className="ri-fingerprint-line" />
          </span>
          <div>
            <h2 className="font-heading text-base font-semibold text-foreground-950">离线激活码生成器</h2>
            <p className="text-xs text-foreground-500">
              非对称签名方案：后台私钥签名、软件内置公钥验签；有效期可选，留空表示永久有效。
            </p>
          </div>
        </div>

        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          <label className="flex flex-col gap-1.5">
            <span className="text-xs font-medium text-foreground-700">客户机器码 *</span>
            <input
              type="text"
              value={machineCode}
              onChange={(event) => setMachineCode(event.target.value)}
              placeholder="例如：A1B2-C3D4-E5F6-7890"
              className="rounded-md border border-background-300 bg-background-50 px-3 py-2 font-mono text-sm text-foreground-900 focus:border-primary-400 focus:outline-none"
            />
          </label>
          <label className="flex flex-col gap-1.5">
            <span className="text-xs font-medium text-foreground-700">有效期（可选）</span>
            <input
              type="date"
              value={expiresAt}
              onChange={(event) => setExpiresAt(event.target.value)}
              className="rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm text-foreground-900 focus:border-primary-400 focus:outline-none"
            />
          </label>
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-3">
          <button
            type="button"
            disabled={generating}
            onClick={() => void handleGenerate()}
            className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <span className="flex h-4 w-4 items-center justify-center">
              <i className="ri-key-2-line" />
            </span>
            {generating ? "生成中…" : "生成激活码"}
          </button>
          <span className="text-[11px] text-foreground-500">机器码大小写不敏感，生成前会自动统一为大写。</span>
        </div>

        {result && (
          <div className="mt-4 rounded-md border border-primary-200 bg-primary-50 p-4">
            <p className="text-xs text-foreground-600">
              绑定机器码 <span className="font-mono text-foreground-900">{result.machineCode}</span> ·{" "}
              {result.permanent ? "永久有效" : "有效期已写入激活码"}
            </p>
            <div className="mt-2 rounded-md border border-primary-300 bg-background-50 px-3 py-2.5">
              <code className="block break-all font-mono text-sm leading-relaxed tracking-wide text-primary-700">
                {result.code}
              </code>
            </div>
            <div className="mt-2 flex flex-wrap items-center gap-3">
              <button
                type="button"
                onClick={() => void copy(result.code, "激活码")}
                className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-primary-300 bg-background-50 px-3 py-1.5 text-xs font-medium text-primary-700 transition-colors hover:bg-primary-100"
              >
                <span className="flex h-3.5 w-3.5 items-center justify-center">
                  <i className="ri-file-copy-line" />
                </span>
                复制激活码
              </button>
              <span className="text-[11px] text-foreground-500">激活码较长属正常现象（含 ECDSA 签名），整段复制即可。</span>
            </div>
            <p className="mt-2 text-[11px] leading-relaxed text-foreground-500">
              把该激活码发给客户，客户在软件里粘贴即可离线完成验证，无需联网。
            </p>
          </div>
        )}
      </div>

      <div className="rounded-lg border border-background-200 bg-secondary-50 p-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <span className="flex h-8 w-8 items-center justify-center rounded-md bg-secondary-100 text-secondary-900">
              <i className="ri-shield-keyhole-line" />
            </span>
            <div>
              <h2 className="font-heading text-base font-semibold text-foreground-950">离线验签公钥</h2>
              <p className="text-xs text-foreground-600">打包软件内置此公钥即可离线验签；私钥保存在后台，永不外泄。</p>
            </div>
          </div>
          <button
            type="button"
            disabled={revealing}
            onClick={() => void handleReveal()}
            className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-secondary-300 bg-background-50 px-3 py-1.5 text-xs font-medium text-secondary-900 transition-colors hover:bg-secondary-100 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <span className="flex h-3.5 w-3.5 items-center justify-center">
              <i className={publicKey ? "ri-eye-off-line" : "ri-eye-line"} />
            </span>
            {publicKey ? "隐藏公钥" : revealing ? "获取中…" : "查看公钥"}
          </button>
        </div>

        {publicKey && (
          <div className="mt-3">
            <div className="flex flex-wrap items-center gap-3">
              <code className="block max-h-40 w-full overflow-y-auto break-all rounded-md border border-secondary-200 bg-background-50 px-3 py-2 font-mono text-xs leading-relaxed text-foreground-900">
                {publicKey}
              </code>
            </div>
            <div className="mt-2 flex flex-wrap items-center gap-3">
              <button
                type="button"
                onClick={() => void copy(publicKey, "离线验签公钥")}
                className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-secondary-300 bg-background-50 px-3 py-1.5 text-xs font-medium text-secondary-900 transition-colors hover:bg-secondary-100"
              >
                <span className="flex h-3.5 w-3.5 items-center justify-center">
                  <i className="ri-file-copy-line" />
                </span>
                复制公钥
              </button>
              <span className="text-[11px] text-foreground-500">把这段 JWK 公钥内置进客户端，配合文档页的验签示例使用。</span>
            </div>
            <p className="mt-2 text-[11px] leading-relaxed text-accent-700">
              提示：公钥可安全公开；私钥仅存于后台，用于生成激活码，请勿在任何客户端放置私钥。
            </p>
          </div>
        )}
      </div>
    </div>
  );
}